import type { PointerEvent as ReactPointerEvent } from 'react';

export type PointerSessionHandlers = {
  onDragStart?: (x: number, y: number) => void;
  onDragMove?: (x: number, y: number) => void;
  onDragEnd?: (x: number, y: number) => void;
  onDragCancel?: () => void;
};

const DRAG_THRESHOLD_PX = 5;

/** Start a document-level pointer session. Left or right mouse, or touch. */
export function startPointerSession(
  event: ReactPointerEvent,
  handlers: PointerSessionHandlers,
  options?: { immediate?: boolean }
): void {
  if (event.button !== 0 && event.button !== 2) return;

  const pointerId = event.pointerId;
  const pointerType = event.pointerType;
  const startX = event.clientX;
  const startY = event.clientY;
  let mode: 'pending' | 'drag' | 'scroll' = options?.immediate ? 'drag' : 'pending';

  const cleanup = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onCancel);
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('contextmenu', onContextMenu, true);
  };

  const onContextMenu = (ev: Event) => {
    ev.preventDefault();
  };

  const onMove = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    const dx = ev.clientX - startX;
    const dy = ev.clientY - startY;

    if (mode === 'pending') {
      if (pointerType === 'touch' && Math.abs(dy) > 8 && Math.abs(dy) >= Math.abs(dx)) {
        mode = 'scroll';
        cleanup();
        return;
      }
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      mode = 'drag';
      handlers.onDragStart?.(ev.clientX, ev.clientY);
    }

    if (mode === 'drag') {
      ev.preventDefault();
      handlers.onDragMove?.(ev.clientX, ev.clientY);
    }
  };

  const onUp = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    cleanup();
    if (mode === 'drag') handlers.onDragEnd?.(ev.clientX, ev.clientY);
  };

  const onCancel = (ev: PointerEvent) => {
    if (ev.pointerId !== pointerId) return;
    cleanup();
    if (mode === 'drag') handlers.onDragCancel?.();
  };

  const onKey = (ev: KeyboardEvent) => {
    if (ev.key !== 'Escape') return;
    cleanup();
    if (mode === 'drag') handlers.onDragCancel?.();
    mode = 'scroll';
  };

  if (mode === 'drag') handlers.onDragStart?.(startX, startY);

  window.addEventListener('pointermove', onMove, { passive: false });
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onCancel);
  window.addEventListener('keydown', onKey);
  window.addEventListener('contextmenu', onContextMenu, true);
}
